<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  listDirectoryChanges,
  approveDirectoryChange,
  rejectDirectoryChange,
  type DirectoryChangeItem,
} from '../api';

type StatusTab = 'PENDING' | 'APPROVED' | 'REJECTED';
const tab = ref<StatusTab>('PENDING');
const loading = ref(false);
const list = ref<DirectoryChangeItem[]>([]);
const current = ref<DirectoryChangeItem | null>(null);

async function reload() {
  loading.value = true;
  try {
    list.value = await listDirectoryChanges(tab.value);
    current.value = null;
  } catch (e) {
    ElMessage.error(normalizeError(e));
  } finally {
    loading.value = false;
  }
}

function openDetail(item: DirectoryChangeItem) {
  current.value = item;
}

async function onApprove() {
  if (!current.value) return;
  const item = current.value;
  try {
    await ElMessageBox.confirm('确认通过该名片修改?通过后将替换该用户在名录中的展示。', '通过修改', {
      confirmButtonText: '通过',
      cancelButtonText: '取消',
      type: 'success',
    });
  } catch {
    return;
  }
  try {
    await approveDirectoryChange(item.id);
    ElMessage.success('已通过,名片修改已生效');
    await reload();
  } catch (e) {
    ElMessage.error(normalizeError(e));
  }
}

async function onReject() {
  if (!current.value) return;
  const item = current.value;
  try {
    const { value } = await ElMessageBox.prompt('请填写驳回原因(用户会看到)', '驳回修改', {
      inputType: 'textarea',
      inputPlaceholder: '如:联系方式格式异常 / 简介含推广信息 / 姓名与认证不符',
      inputValidator: (v) => (v && v.trim().length >= 2 ? true : '至少 2 字'),
      confirmButtonText: '驳回',
      cancelButtonText: '取消',
    });
    if (!value) return;
    await rejectDirectoryChange(item.id, value.trim());
    ElMessage.success('已驳回');
    await reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel') return;
    ElMessage.error(normalizeError(e));
  }
}

function normalizeError(e: unknown): string {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}

function formatTime(s: string | null): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

onMounted(() => {
  reload();
});
</script>

<template>
  <div class="audit-pane">
    <el-tabs v-model="tab" @tab-change="reload" class="tabs">
      <el-tab-pane label="待审核" name="PENDING" />
      <el-tab-pane label="已通过" name="APPROVED" />
      <el-tab-pane label="已驳回" name="REJECTED" />
    </el-tabs>

    <div class="layout">
      <div class="list" v-loading="loading">
        <el-empty v-if="!list.length && !loading" description="暂无记录" />
        <div
          v-for="item of list"
          :key="item.id"
          class="list-item"
          :class="{ active: current?.id === item.id }"
          @click="openDetail(item)"
        >
          <div class="list-row">
            <span class="name">{{ item.snapshot.realName || item.user?.displayName || '未命名' }}</span>
            <span class="school">{{ item.user?.schoolName || '-' }}</span>
          </div>
          <div class="list-sub">
            <span>{{ item.snapshot.industry || '未填行业' }}</span>
            <span>·</span>
            <span>{{ formatTime(item.createdAt) }}</span>
          </div>
          <div v-if="item.status === 'REJECTED' && item.rejectReason" class="list-reject">
            驳回原因:{{ item.rejectReason }}
          </div>
        </div>
      </div>

      <div class="detail">
        <div v-if="!current" class="detail-empty">
          <el-empty description="选一条记录查看名片修改内容" />
        </div>
        <template v-else>
          <div class="detail-header">
            <div class="title">{{ current.snapshot.realName || current.user?.displayName || '未命名' }}</div>
            <div class="subtitle">
              用户 ID <code>{{ current.user?.id }}</code> · 提交于 {{ formatTime(current.createdAt) }}
            </div>
          </div>

          <el-descriptions :column="1" border class="desc">
            <el-descriptions-item label="姓名">{{ current.snapshot.realName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="行业">{{ current.snapshot.industry || '-' }}</el-descriptions-item>
            <el-descriptions-item label="自定义标签">{{ current.snapshot.industryCustomTag || '-' }}</el-descriptions-item>
            <el-descriptions-item label="公司">{{ current.snapshot.company || '-' }}</el-descriptions-item>
            <el-descriptions-item label="职位">{{ current.snapshot.title || '-' }}</el-descriptions-item>
            <el-descriptions-item label="简介">{{ current.snapshot.bio || '-' }}</el-descriptions-item>
            <el-descriptions-item label="名录展示">{{ current.snapshot.directoryVisible ? '是' : '否' }}</el-descriptions-item>
            <el-descriptions-item label="微信">{{ current.snapshot.contact.wechatId || '-' }}</el-descriptions-item>
            <el-descriptions-item label="手机">{{ current.snapshot.contact.mobile || '-' }}</el-descriptions-item>
            <el-descriptions-item label="邮箱">{{ current.snapshot.contact.email || '-' }}</el-descriptions-item>
            <el-descriptions-item label="备注">{{ current.snapshot.contact.note || '-' }}</el-descriptions-item>
          </el-descriptions>

          <div v-if="current.status === 'PENDING'" class="detail-actions">
            <el-button type="success" size="large" @click="onApprove">通过</el-button>
            <el-button type="danger" size="large" @click="onReject">驳回</el-button>
          </div>
          <div v-else class="detail-handled">
            <span v-if="current.status === 'APPROVED'" class="tag-approved">✓ 已通过</span>
            <span v-else-if="current.status === 'REJECTED'" class="tag-rejected">
              ✕ 已驳回:{{ current.rejectReason }}
            </span>
            <div class="muted" v-if="current.reviewedAt">处理时间:{{ formatTime(current.reviewedAt) }}</div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.audit-pane { display: flex; flex-direction: column; height: calc(100vh - 80px); }
.tabs { margin-bottom: 12px; }
.layout { display: flex; flex: 1; gap: 16px; overflow: hidden; }
.list { width: 340px; overflow-y: auto; background: #fff; border-radius: 8px; padding: 8px; }
.list-item { padding: 12px; border-radius: 6px; cursor: pointer; margin-bottom: 6px; border: 1px solid transparent; }
.list-item:hover { background: #f5f5f7; }
.list-item.active { background: #e6f4ff; border-color: #409eff; }
.list-row { display: flex; justify-content: space-between; font-weight: 600; }
.name { color: #1d1d1f; }
.school { color: #86868b; font-size: 13px; }
.list-sub { margin-top: 4px; font-size: 12px; color: #86868b; display: flex; gap: 6px; flex-wrap: wrap; }
.list-reject { margin-top: 6px; font-size: 12px; color: #b42020; }
.detail { flex: 1; overflow-y: auto; background: #fff; border-radius: 8px; padding: 24px; }
.detail-empty { height: 100%; display: flex; align-items: center; justify-content: center; }
.detail-header .title { font-size: 22px; font-weight: 600; }
.detail-header .subtitle { margin-top: 6px; color: #666; font-size: 13px; }
.detail-header .subtitle code { background: #f5f5f7; padding: 2px 6px; border-radius: 4px; font-size: 12px; }
.desc { margin-top: 20px; }
.detail-actions { margin-top: 28px; display: flex; gap: 16px; }
.detail-handled { margin-top: 24px; }
.tag-approved { color: #1e7c3e; font-size: 18px; font-weight: 600; }
.tag-rejected { color: #b42020; font-size: 16px; }
.muted { color: #86868b; font-size: 13px; margin-top: 8px; }
</style>
